import { renderStepText, type SourceRef, type StepBody } from "@typedoc/contracts/processes";
import type {
  CitedSourceView,
  DraftView,
  ProcessView,
  ReleaseView,
} from "@typedoc/contracts/processes/contract";
import type { MemberRole } from "@typedoc/contracts/member-sync";
import { z } from "zod";
import { recordingTitle } from "../../components/recordings/chat-recording";
import type { LiveDraft } from "../processes/live-draft";
import { stepNumber } from "../processes/steps-view";

// A playbook's own page (decision `2026-11-02-a-playbook-opens-on-its-overview-and-changing-docks-
// the-chat`, Paper PO1 to PO6b, ENG-1067): its tabs and their addresses, who may change it, and
// what the Overview says, read from the published version. The page draws them.

/** The tabs, each its own address under `/playbooks/:id`, so Back returns to the tab. */
export const PLAYBOOK_TABS = ["overview", "steps", "connections", "activity"] as const;
export type PlaybookTab = (typeof PLAYBOOK_TABS)[number];

/**
 * What every tab's address carries: the chat docked beside the playbook, by its ID, or `new` for a
 * chat with Abacor not started yet. Absent, the page is viewing. A value this build does not know
 * is dropped, so an old link opens the page viewing rather than an error.
 */
export const playbookPageSearchSchema = z.object({
  chat: z
    .string()
    .regex(/^[a-z0-9_-]{1,64}$/i)
    .optional()
    .catch(undefined),
  /**
   * The playbook was opened from the docked chat (its card or the shelf), so the chat is the work
   * and the playbook is what closes. Absent, the chat was opened from the playbook and closes.
   */
  from: z.literal("chat").optional().catch(undefined),
});
export type PlaybookPageSearch = z.infer<typeof playbookPageSearchSchema>;

/**
 * The Steps tab also carries the step open in place, by its ID, and the Map when it shows the
 * steps as one (the list is the default and has no word in the address).
 */
export const playbookStepsSearchSchema = playbookPageSearchSchema.extend({
  step: z
    .string()
    .regex(/^step_[0-9a-z]{26}$/)
    .optional()
    .catch(undefined),
  /** A table to unfold under the steps, by its ID: a chat card's table chip asks for it. */
  table: z
    .string()
    .regex(/^dtbl_[0-9a-z]{26}$/)
    .optional()
    .catch(undefined),
  view: z.literal("map").optional().catch(undefined),
});
export type PlaybookStepsSearch = z.infer<typeof playbookStepsSearchSchema>;

/**
 * What the header's one dark button does for this person (decision items 9 and 10, PR #196):
 *
 * - `change`: they may change it: the playbook's maker or an admin with no one else's draft open,
 *   or the creator of the open draft. Change with Abacor docks the chat and shows the draft.
 * - `others-draft`: someone else's draft is open. Change with Abacor docks the chat, which answers
 *   questions and says changes wait until that draft is published or discarded.
 * - `ask`: they may not change it. Ask Abacor docks the same chat, which answers questions about
 *   the published version and says who can change it.
 *
 * An admin may still edit someone else's open draft in place; the steps decide that on their own
 * (`mayEditDraft`), since a chat can't act as an admin.
 */
export type ChangeMode =
  | { kind: "change" }
  | { kind: "others-draft"; creatorId: string }
  | { kind: "ask" };

const isAdmin = (role: MemberRole | null) => role === "owner" || role === "admin";

export function changeModeOf({
  viewerId,
  role,
  makerId,
  openDraft,
}: {
  viewerId: string;
  role: MemberRole | null;
  makerId: string;
  openDraft: Pick<DraftView, "createdBy"> | null;
}): ChangeMode {
  if (viewerId === "") return { kind: "ask" };
  if (openDraft && openDraft.createdBy === viewerId) return { kind: "change" };
  if (openDraft) return { kind: "others-draft", creatorId: openDraft.createdBy };
  return viewerId === makerId || isAdmin(role) ? { kind: "change" } : { kind: "ask" };
}

/** The header's button: Ask Abacor for someone who may not change it, else Change with Abacor. */
export const changeLabel = (mode: ChangeMode) =>
  mode.kind === "ask" ? "Ask Abacor" : "Change with Abacor";

/**
 * A published version as the steps list draws a draft: its steps, pins and facts, closed as
 * released, so nothing on it can be changed and every step reads as the version has it.
 */
export function releaseAsDraft(process: ProcessView, release: ReleaseView): LiveDraft {
  const { content } = release;
  return {
    process,
    draft: {
      id: release.fromDraftId,
      processId: process.id,
      baseReleaseId: release.id,
      revision: release.fromRevision,
      status: "released",
      facts: content.facts,
      factsHash: content.factsHash,
      factsBaseHash: content.factsHash,
      createdBy: release.releasedBy,
      createdAt: release.createdAt,
    },
    steps: content.steps.map((step) => ({ ...step, baseHash: step.hash })),
    pins: (content.tables ?? []).map(({ tableId, hash, ...pinned }) => ({
      tableId,
      content: pinned,
      hash,
      baseHash: hash,
    })),
    changes: [],
    revision: release.fromRevision,
    stale: false,
    // A published version has no undo stack: nothing on it can be changed.
    stack: null,
  };
}

/** One step as the Overview refers to it: its number, for "made by 06" and the rail's details. */
type Numbered = { stepId: string; number: string; body: StepBody };

const numbered = (release: ReleaseView): Numbered[] =>
  release.content.steps.map((step, index) => ({
    stepId: step.stepId,
    number: stepNumber(index),
    body: step.body,
  }));

/** Step numbers as the rail says them: "step 08", "2 steps". */
export const stepsDetail = (numbers: readonly string[]) =>
  numbers.length === 1 ? `step ${numbers[0] ?? ""}` : `${String(numbers.length)} steps`;

/**
 * What the Overview says the playbook delivers: the names of what one case makes, in the order the
 * version names them, and the steps that make them. Null when the version names none.
 */
export function deliversOf(release: ReleaseView): {
  names: string[];
  madeBy: { stepId: string; number: string }[];
  caseUnit: string | null;
} | null {
  const refs = release.content.facts.delivers?.value ?? [];
  const steps = numbered(release);
  const names: string[] = [];
  const madeBy = new Map<string, string>();
  for (const ref of refs) {
    const step = steps.find((each) => each.stepId === ref.stepId);
    const output = step?.body.outputs.find((each) => each.key === ref.key);
    if (!step || !output) continue;
    names.push(output.label);
    madeBy.set(step.stepId, step.number);
  }
  const caseUnit = release.content.facts.caseUnit?.value.trim() || null;
  if (names.length === 0) return null;
  return {
    names,
    madeBy: [...madeBy.entries()]
      .map(([stepId, number]) => ({ stepId, number }))
      .sort((a, b) => a.number.localeCompare(b.number)),
    caseUnit: caseUnit ? caseUnit.replace(/^(a|an|one|each)\s+/i, "") : null,
  };
}

/**
 * What the playbook uses, one row per thing named, with the steps that use it. Read from what
 * each step says it uses; the account picker (ENG-1064) makes these structured, with the account.
 */
export function usesOf(
  release: ReleaseView,
): { name: string; numbers: string[]; firstStepId: string }[] {
  const rows = new Map<string, { name: string; numbers: string[]; firstStepId: string }>();
  for (const step of numbered(release)) {
    const said = step.body.answers.uses?.value.trim();
    if (!said) continue;
    const name = renderStepText(step.body, said);
    const key = name.toLowerCase();
    const row = rows.get(key);
    if (row) row.numbers.push(step.number);
    else rows.set(key, { name, numbers: [step.number], firstStepId: step.stepId });
  }
  return [...rows.values()];
}

/** Every source a step's values cite: its sentence, its answers and its exact values. */
function sourcesOf(body: StepBody): SourceRef[] {
  const answers = Object.values(body.answers).flatMap((answer) => answer?.sources ?? []);
  const slots = Object.values(body.slots ?? {}).flatMap((slot) => slot.sources);
  return [...body.title.sources, ...answers, ...slots];
}

/** A file or recording a published version cites, with the steps that cite it. */
export type BuiltFromRow = {
  kind: "file" | "recording";
  id: string;
  numbers: string[];
  /** The first step that cites it, where its row opens. */
  firstStepId: string;
};

/**
 * Built from: the files and recordings the version's values cite, each with the steps that cite it,
 * files first. Messages in a chat are not listed: they are the chat, not a source someone opens.
 */
export function builtFromOf(release: ReleaseView): BuiltFromRow[] {
  const rows = new Map<string, BuiltFromRow>();
  for (const step of numbered(release)) {
    for (const source of sourcesOf(step.body)) {
      if (source.kind === "message") continue;
      const row = rows.get(source.id) ?? {
        kind: source.kind,
        id: source.id,
        numbers: [],
        firstStepId: step.stepId,
      };
      if (!row.numbers.includes(step.number)) row.numbers.push(step.number);
      rows.set(source.id, row);
    }
  }
  return [...rows.values()].sort((a, b) => (a.kind === b.kind ? 0 : a.kind === "file" ? -1 : 1));
}

/**
 * What a Built from row says (ENG-1068): a file's own name and type, or what a recording's maker
 * said they would show ("Screen recording" when they said nothing). One the person may not open,
 * or one deleted since, says so instead of vanishing. Before the names are read, or when they
 * couldn't be, `named` is undefined and each row is named by its kind alone.
 */
export function builtFromName(
  row: Pick<BuiltFromRow, "kind" | "id">,
  named: readonly CitedSourceView[] | undefined,
): { name: string; mediaType: string | null; known: boolean } {
  const found = named?.find((source) => source.id === row.id);
  const word = row.kind === "file" ? "file" : "recording";
  if (!found) return { name: `A ${word}`, mediaType: null, known: false };
  if (found.state === "hidden") {
    return { name: `A ${word} you can't open`, mediaType: null, known: false };
  }
  if (found.state === "gone") {
    return { name: `A ${word} deleted since`, mediaType: null, known: false };
  }
  if (row.kind === "file") {
    return { name: found.name ?? "A file", mediaType: found.mediaType, known: true };
  }
  return { name: recordingTitle({ intent: found.name }), mediaType: null, known: true };
}

/** "Oct 30", for the standing line and the Version rows. */
export const dayOf = (at: string) =>
  new Date(at).toLocaleDateString("en-US", { month: "short", day: "numeric" });

/** The address a playbook opens at from anywhere: its Overview, or Steps with the chat for a first draft. */
export function playbookTarget(playbook: {
  id: string;
  published: boolean;
  chatId: string | null;
}):
  | { to: "/playbooks/$playbookId"; params: { playbookId: string } }
  | {
      to: "/playbooks/$playbookId/steps";
      params: { playbookId: string };
      search: { chat: string };
    } {
  const params = { playbookId: playbook.id };
  if (playbook.published) return { to: "/playbooks/$playbookId", params };
  return { to: "/playbooks/$playbookId/steps", params, search: { chat: playbook.chatId ?? "new" } };
}
